import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, type ButtonSize, type ButtonVariant } from '../forms/Button';
import { safeWriteClipboard } from '../../lib/utils/clipboard';

/** How long the button stays in its "Copied" state before reverting. */
export const COPY_FEEDBACK_MS = 2000;

export interface CopyTextButtonProps {
  /**
   * The COMPLETE text to place on the clipboard.
   *
   * Always pass the full value, never what the container happens to be
   * rendering — these buttons sit on blocks that are frequently truncated
   * (`line-clamp-*`, collapsed cards, scroll boxes) and the user means "copy
   * the whole thing", not "copy the visible preview".
   */
  readonly text: string;
  /**
   * What is being copied, lowercase noun phrase (e.g. "journalist query").
   * Builds the aria-label, the screen-reader announcement, and the failure
   * message, so it must read naturally inside a sentence.
   */
  readonly subject: string;
  /** Visible label in the idle state. */
  readonly label?: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly className?: string;
}

/**
 * Copy-to-clipboard button with transient "Copied" feedback.
 *
 * Wraps `safeWriteClipboard` (async Clipboard API + legacy fallback for
 * unfocused/insecure contexts). A rejected write is never silent — it surfaces
 * a toast, matching how the rest of the app reports clipboard failures.
 *
 * Defaults to `outline` (the design system's "utility action" variant, see the
 * Button table in CLAUDE.md) at `lg`, the only Button size meeting the 44px
 * minimum touch target required by docs/design-system/elevated-polish-guide.md.
 */
export function CopyTextButton({
  text,
  subject,
  label = 'Copy',
  variant = 'outline',
  size = 'lg',
  className = '',
}: CopyTextButtonProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  // These buttons live inside list cards that unmount on expand/collapse and
  // on optimistic list removal, so both the revert timer and an in-flight
  // clipboard promise routinely outlive the component.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  // `useSwipeGesture` binds `pointerdown` NATIVELY on the swipeable card
  // element. React handlers are delegated from the root container, so a
  // synthetic `onPointerDown` here would fire AFTER the card's own listener —
  // too late to stop it. Only a native listener bound to this subtree runs
  // first, which is what keeps a press/drag starting on the copy button from
  // driving the card's swipe actions (open compose / open reject form).
  const pointerGuardCleanup = useRef<(() => void) | null>(null);
  const attachPointerGuard = useCallback((node: HTMLSpanElement | null) => {
    if (pointerGuardCleanup.current) {
      pointerGuardCleanup.current();
      pointerGuardCleanup.current = null;
    }
    if (!node) return;
    const stop = (e: Event) => e.stopPropagation();
    node.addEventListener('pointerdown', stop);
    pointerGuardCleanup.current = () => node.removeEventListener('pointerdown', stop);
  }, []);

  const handleCopy = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      // Copy buttons sit inside click-to-expand cards; copying must not also
      // toggle the container out from under the user.
      e.stopPropagation();

      const ok = await safeWriteClipboard(text);
      if (!isMounted.current) return;

      if (!ok) {
        setCopied(false);
        // Deliberately does NOT say "select the text and copy it manually" —
        // the HARO cards carry `select-none` (they are swipeable), so manual
        // selection is not available there. Advice the user cannot act on is
        // worse than none.
        toast.error(`Could not copy the ${subject} — your browser blocked clipboard access.`);
        return;
      }

      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    },
    [subject, text]
  );

  // Nothing to copy — a button that silently puts an empty string on the
  // clipboard is worse than no button.
  if (!text) return null;

  return (
    // `contents` keeps this wrapper out of the layout box tree, so the Button
    // stays a direct flex item of the caller's row (ml-auto / justify-end keep
    // working) while still sitting on the DOM path the pointer guard needs.
    <span ref={attachPointerGuard} className="contents">
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleCopy}
        // The surrounding card toggles expand on Enter/Space; keep keyboard
        // activation of this button from bubbling into that handler.
        onKeyDown={(e) => e.stopPropagation()}
        // Intentionally constant across states. The live region below is the
        // single spoken confirmation — changing the accessible name of the
        // focused button too would announce the same event twice, in two
        // different phrasings. A stable name also keeps the persistent visible
        // label ("Copy") inside the accessible name for speech-input users
        // (WCAG 2.5.3 Label in Name).
        aria-label={`Copy ${subject} to clipboard`}
        className={className}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <Copy className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        )}
        {copied ? 'Copied' : label}
      </Button>
      {/* role="status" already implies aria-live="polite" + aria-atomic. */}
      <span className="sr-only" role="status">
        {copied ? `${subject} copied to clipboard` : ''}
      </span>
    </span>
  );
}
